import { getVariantsSortedByPrice } from '../../../products';
import { calculateDiscountedPrice } from '../../../offer/helpers';
import { getLowestCostVariant, getRecipeCombination } from '../functions';

export const resolveRecipeConfig = ({
  combinationsByPlanType,
  selectedMealPrepType,
  selectedFreshRecipes,
  selectedBakedRecipes,
  offerType,
  offerAmount,
}) => {
  if (
    selectedMealPrepType === 'fresh' ||
    selectedMealPrepType === 'half_fresh'
  ) {
    const product = combinationsByPlanType[selectedMealPrepType];

    if (!selectedFreshRecipes.length) {
      return { price: null, message: 'Choose one or more recipes.' };
    }

    const variants =
      product && Array.isArray(product.planCombinations)
        ? getVariantsSortedByPrice(product.planCombinations)
        : [];
    const newVariant = getRecipeCombination(variants, {
      fresh: selectedFreshRecipes,
      baked: [],
    });

    if (!newVariant) {
      return {
        price: null,
        message: 'No matching recipe combination found.',
      };
    }

    const price = newVariant.price_per_week;
    const discountedPrice = calculateDiscountedPrice(
      price,
      offerType,
      offerAmount,
    );

    return {
      price,
      discountedPrice,
      combination_id: newVariant.combination_id,
      discountAmount: offerAmount,
    };
  }

  const product = combinationsByPlanType.mixed;

  if (!selectedFreshRecipes.length) {
    return {
      price: null,
      message: 'Choose one or more fresh recipes.',
    };
  }

  if (!selectedBakedRecipes.length) {
    return {
      price: null,
      message: 'Choose one or more baked recipes.',
    };
  }

  const variants = getVariantsSortedByPrice(product.planCombinations);
  const newVariant = getRecipeCombination(variants, {
    fresh: selectedFreshRecipes,
    baked: selectedBakedRecipes,
  });

  if (!newVariant) {
    return {
      price: null,
      message: 'No matching recipe combination found.',
    };
  }

  const price = newVariant.price_per_week;
  const discountedPrice = calculateDiscountedPrice(
    price,
    offerType,
    offerAmount,
  );

  return {
    price,
    discountedPrice,
    combination_id: newVariant.combination_id,
    discountAmount: offerAmount,
  };
};

export const didAllergiesChange = (
  petAllergies,
  submittedAllergies,
  allergyAnswer,
) => {
  const current = (petAllergies ?? []).map(a => a.name).sort();
  const submitted =
    allergyAnswer === 'true'
      ? (submittedAllergies ?? []).map(a => a.label).sort()
      : [];

  return (
    current.length !== submitted.length ||
    current.some((name, index) => name !== submitted[index])
  );
};

export const buildProductTypeInterpolationSubstitutes = ({
  combinationsByPlanType,
  offerType,
  offerAmount,
}) => {
  const lowestMixedVariant = combinationsByPlanType?.mixed?.planCombinations
    ?.length
    ? getLowestCostVariant(combinationsByPlanType.mixed.planCombinations)
    : null;
  const lowestFreshVariant = combinationsByPlanType?.fresh?.planCombinations
    ?.length
    ? getLowestCostVariant(combinationsByPlanType.fresh.planCombinations)
    : null;
  const lowestHalfFreshVariant = combinationsByPlanType?.half_fresh
    ?.planCombinations?.length
    ? getLowestCostVariant(combinationsByPlanType.half_fresh.planCombinations)
    : null;
  const lowestBakedVariant = combinationsByPlanType?.dry?.planCombinations
    ?.length
    ? getLowestCostVariant(combinationsByPlanType.dry.planCombinations)
    : null;

  return {
    freshPricePerWeek: lowestFreshVariant?.price_per_week,
    freshDiscountPricePerWeek: calculateDiscountedPrice(
      lowestFreshVariant?.price_per_week,
      offerType,
      offerAmount,
    ),
    mixedPricePerWeek: lowestMixedVariant?.price_per_week,
    mixedDiscountPricePerWeek: calculateDiscountedPrice(
      lowestMixedVariant?.price_per_week,
      offerType,
      offerAmount,
    ),
    halfFreshPricePerWeek: lowestHalfFreshVariant?.price_per_week,
    halfFreshDiscountPricePerWeek: calculateDiscountedPrice(
      lowestHalfFreshVariant?.price_per_week,
      offerType,
      offerAmount,
    ),
    bakedPricePerWeek: lowestBakedVariant?.price_per_week,
    bakedDiscountPricePerWeek: calculateDiscountedPrice(
      lowestBakedVariant?.price_per_week,
      offerType,
      offerAmount,
    ),
  };
};

export const getNextBoxCheckoutDetails = (
  selectedPlanTypeCombinations,
  combinationId,
) => {
  const selectedCombination =
    selectedPlanTypeCombinations?.planCombinations?.find(
      combo => combo.combination_id === combinationId,
    );

  const selectedPlanDefaultCadence =
    selectedPlanTypeCombinations?.cadence ?? null;
  const subscriptionPrice =
    selectedCombination?.subscription_price?.box_price ?? null;
  const starterBoxPrice = selectedCombination?.price ?? null;

  return {
    defaultCadence: selectedPlanDefaultCadence,
    subscriptionPrice,
    starterBoxPrice,
  };
};
